import { BookingTrackingResult, BookingStatus, LightweightBookingStatus } from "../../Services/BookingEntities";
import { BookingInfo, BookingTrackingDetails } from "./MyBookingEntities";
import { GbBookingInfo, GbBookingStatus, GbLightweightBookingStatus } from "../../Services/GbBookingEntities";
import moment from "moment";

/**
 * Convert BookingTrackingResult API contract in to BookingInfo data model.
 * */
export function ConvertBookingTrackingResultToBookingInfo(booking: BookingTrackingResult): BookingInfo {

    return {
        Time: booking.Time,
        LocalTime: booking.LocalTime,
        MaxSeat: booking.MaxSeat,
        CarConditionID: booking.CarConditionID,
        ServiceOffering: booking.ServiceOffering,
        DispatchBookingID: booking.DispatchBookingID.toString(),
        BookingID: booking.BookingID,
        AccessCode: booking.HashCode,
        PassengerName: booking.PassengerName,
        DataOwnership: booking.DataOwnership,
        ContactName: booking.ContactName,
        NotesToDriver: booking.NotesToDriver,
        OrderNumber: booking.OrderNumber,
        AccountNumber: booking.AccountNumber,
        CustomerPhone: booking.CustomerPhone,
        ReadAccessCode: booking.ReadAccessCode,
        BookingTrackingBaseUrl: booking.BookingTrackingBaseUrl,
        IsDeliveryBooking: booking.IsDeliveryBooking,
        ApiVersion: 1,
        GoogleOdrdTripId: booking.GoogleOdrdTripId,
        TrackingInfo: ConvertToBookingTrackingDetails(booking),
        Locations: booking.Locations,
    }
}

/**
 * Convert BookingTrackingResult API contract array in to BookingInfo data model array.
 * */
export function ConvertBookingTrackingResultToBookingInfoBulk(bookings: BookingTrackingResult[]): BookingInfo[] {
    return bookings.map(ConvertBookingTrackingResultToBookingInfo);
}

/**
 * Convert BookingTrackingResult API contract in to BookingTrackingDetails data model.
 * */
function ConvertToBookingTrackingDetails(booking: BookingTrackingResult): BookingTrackingDetails {

    const trackingDetails: BookingTrackingDetails = {
        BookingID: booking.BookingID,
        VehicleLocation: null,
        CarNumber: booking.CarNumber ?? null,
        DriverName: booking.DriverName,
        DriverImage: booking.DriverImage,
        DriverID: booking.DriverID ?? null,
        BookingStatusID: booking.BookingStatusID,
        DispatchStartTime: null,
        DropoffTime: null,
        PickedupTime: null,
        ProofOfDeliveryUrls: null,
        DriverPhotoExists: true,
        WasJitPreauthFailure: booking.WasJitPreauthFailure,
        JitPreauthFailureMessage: booking.JitPreauthFailureMessage
    }

    // vehicle location can be undefined or an empty array.
    if (booking.vechileLocation && booking.vechileLocation.length > 0) {
        // Most recent location is the last element of the array
        trackingDetails.VehicleLocation = booking.vechileLocation[booking.vechileLocation.length - 1];
    }

    return trackingDetails;
}

/**
 * Convert GbBookingInfo API contract in to BookingInfo data model.
 * */
export function ConvertGbBookingInfoToBookingInfo(booking: GbBookingInfo): BookingInfo {

    const bookingV1: BookingInfo = {
        BookingID: parseInt(booking.InternalBookingId),
        Time: moment(booking.RequestedDateTime).format("MM/DD/YYYY HH:mm:ss"),
        LocalTime: booking.LocalRequestedTime,
        MaxSeat: "1-4",
        CarConditionID: booking.VehicleType,
        DispatchBookingID: booking.UserBookingReference,
        ContactName: booking.BookingContact.Name,
        PassengerName: booking.Pickup.Contact.Name,
        NotesToDriver: booking.Pickup.DriverInstructions ?? "",
        CustomerPhone: booking.Pickup.Contact.PhoneNumber,
        IsDeliveryBooking: false,
        AccessCode: booking.MetaData.AccessCode,
        ReadAccessCode: booking.MetaData.ReadAccessCode,
        DataOwnership: booking.MetaData.DataOwnership,
        ApiVersion: 2,
        TrackingInfo: ConvertGbBookingInfoToBookingTrackingDetails(booking),
        BookingTrackingBaseUrl: booking.MetaData.BookingTrackingBaseUrl,
        Locations: [// multi-stop support has not been implemented yet for Booking API V2
            {
                AddressText: booking.Pickup.Address.FullTextAddress ?? "",
                GeoPoint: booking.Pickup.Address.GeoLocation
            }
        ],
    };

    if (booking.Dropoff) {
        bookingV1.Locations.push({
            AddressText: booking.Dropoff.Address.FullTextAddress ?? "",
            GeoPoint: booking.Dropoff.Address.GeoLocation
        });
    }

    return bookingV1;
}

/**
 * Convert GbBookingInfo API contract array in to BookingInfo data model array.
 * */
export function ConvertGbBookingInfoToBookingInfoBulk(bookings: GbBookingInfo[]): BookingInfo[] {
    return bookings.map(ConvertGbBookingInfoToBookingInfo);
}

/**
 * Status IDs from Global Booker API is slightly different from the status IDs from V1 (Booking API). But the statuses are mostly similar.
 * A conversion is needed from GbBookingStatus to BookingStatus in order to have a consistent behaviour in the UI.
 */
function ConvertGbBookingStatusToBookingStatus(status: GbBookingStatus): BookingStatus {
    if (status === GbBookingStatus.Pending) return BookingStatus.NotDispatched;
    if (status === GbBookingStatus.Dispatch) return BookingStatus.Dispatching;
    if (status === GbBookingStatus.Assigned) return BookingStatus.Assigned;
    if (status === GbBookingStatus.Accepted) return BookingStatus.Accepted;
    if (status === GbBookingStatus.PickedUp) return BookingStatus.PickedUp;
    if (status === GbBookingStatus.Complete) return BookingStatus.Completed;
    if (status === GbBookingStatus.Cancel) return BookingStatus.Cancelled;
    if (status === GbBookingStatus.NoJob) return BookingStatus.NoJob;
    if (status === GbBookingStatus.NoCarAvailable) return BookingStatus.Cancelled;

    return BookingStatus.NotDispatched;
}

/**
 * Convert GbBookingInfo API contract in to BookingTrackingDetails data model.
 * */
function ConvertGbBookingInfoToBookingTrackingDetails(booking: GbBookingInfo): BookingTrackingDetails {
    return {
        BookingID: parseInt(booking.InternalBookingId),
        DriverName: booking.Driver?.DriverName ?? null,
        DriverImage: null,
        BookingStatusID: ConvertGbBookingStatusToBookingStatus(booking.StatusID),
        CarNumber: booking.CarNumberPlate ?? null,
        DispatchStartTime: null,
        DropoffTime: null,
        PickedupTime: null,
        ProofOfDeliveryUrls: null,
        DriverID: booking.Driver ? parseInt(booking.Driver.DriverIdentifier) : null,
        VehicleLocation: null,
        DriverPhotoExists: booking.Driver?.DriverPhotoExists ?? false,
        WasJitPreauthFailure: false,
        JitPreauthFailureMessage: null
    }
}

/**
 * Convert LightweightBookingStatus API contract (response of the /BookingStatusBulk request) to BookingTrackingDetails data model.
 * */
function ConvertLightweightBookingStatusToBookingTrackingDetails(update: LightweightBookingStatus): BookingTrackingDetails {
    return {
        BookingID: update.BookingId,
        BookingStatusID: update.BookingStatusId,
        CarNumber: update.CarNumberPlate,
        DriverID: update.DriverId,
        DriverImage: null,
        DriverName: null,
        VehicleLocation: update.VehicleLocation,
        DispatchStartTime: null,
        DropoffTime: null,
        PickedupTime: null,
        ProofOfDeliveryUrls: null,
        DriverPhotoExists: true,
        WasJitPreauthFailure: update.WasJitPreauthFailure,
        JitPreauthFailureMessage: update.JitPreauthFailureMessage
    }
}

/**
 * Convert a list of LightweightBookingStatus objects to a list of BookingTrackingDetails objects.
 * */
export function ConvertLightweightBookingStatusToBookingTrackingDetailsBulk(updates: LightweightBookingStatus[]): BookingTrackingDetails[] {
    return updates.map(ConvertLightweightBookingStatusToBookingTrackingDetails);
}

/**
 * Convert GbLightweightBookingStatus API contract (response of the /BookingStatusBulk request) to BookingTrackingDetails data model.
 * */
function ConvertGbLightweightBookingStatusToBookingTrackingDetails(update: GbLightweightBookingStatus): BookingTrackingDetails {
    return {
        BookingID: parseInt(update.InternalBookingId),
        BookingStatusID: ConvertGbBookingStatusToBookingStatus(update.StatusID),
        CarNumber: update.CarNumberPlate,
        DriverID: update.Driver ? parseInt(update.Driver.DriverIdentifier) : null,
        DriverImage: null,
        DriverName: update.Driver?.DriverName ?? null,
        VehicleLocation: null,
        DispatchStartTime: null,
        DropoffTime: moment(update.CompletedDateTime),
        PickedupTime: moment(update.PickedUpDateTime),
        ProofOfDeliveryUrls: null,
        DriverPhotoExists: update.Driver?.DriverPhotoExists ?? false,
        WasJitPreauthFailure: false,
        JitPreauthFailureMessage: null
    }
}

/**
 * Convert a list of GbLightweightBookingStatus objects to a list of BookingTrackingDetails objects.
 * */
export function ConvertGbLightweightBookingStatusToBookingTrackingDetailsBulk(updates: GbLightweightBookingStatus[]): BookingTrackingDetails[] {
    return updates.map(ConvertGbLightweightBookingStatusToBookingTrackingDetails);
}
